import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { Plus, Edit2, Trash2, Truck, Tag } from 'lucide-react';
import '../../../styles/admin/production/Vehicles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_number: '',
    name: '',
    type: 'pickup',
    plate: '',
    service_capabilities: [],
    capacity_notes: '',
    notes: ''
  });
  const [newCapability, setNewCapability] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/vehicles`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() })
      ]);
      setVehicles(vehiclesRes.data);
      setTeams(teamsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const generateVehicleNumber = () => {
    const count = vehicles.length + 1;
    return `VEH-${String(count).padStart(3, '0')}`;
  };

  const handleOpenModal = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        vehicle_number: vehicle.vehicle_number,
        name: vehicle.name,
        type: vehicle.type,
        plate: vehicle.plate || '',
        service_capabilities: vehicle.service_capabilities || [],
        capacity_notes: vehicle.capacity_notes || '',
        notes: vehicle.notes || ''
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        vehicle_number: generateVehicleNumber(),
        name: '',
        type: 'pickup',
        plate: '',
        service_capabilities: [],
        capacity_notes: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingVehicle) {
        await axios.put(
          `${API_URL}/api/vehicles/${editingVehicle.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API_URL}/api/vehicles`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/vehicles/${vehicleId}`,
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAddCapability = () => {
    if (newCapability.trim() && !formData.service_capabilities.includes(newCapability.trim())) {
      setFormData({
        ...formData,
        service_capabilities: [...formData.service_capabilities, newCapability.trim()]
      });
      setNewCapability('');
    }
  };

  const handleRemoveCapability = (capability) => {
    setFormData({
      ...formData,
      service_capabilities: formData.service_capabilities.filter(c => c !== capability)
    });
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : null;
  };

  const vehicleTypeLabels = {
    pickup: 'Pickup',
    van: 'Fourgonnette',
    truck: 'Camion',
    trailer: 'Remorque'
  };

  const statusLabels = {
    active: 'Actif',
    maintenance: 'En maintenance',
    inactive: 'Inactif'
  };

  if (loading) {
    return <div className="vehicles-loading">Chargement...</div>;
  }

  return (
    <div className="vehicles-container">
      <div className="vehicles-header">
        <div>
          <h2>Gestion des Véhicules</h2>
          <p>{vehicles.length} véhicule(s)</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-add">
          <Plus size={20} />
          Nouveau véhicule
        </button>
      </div>

      <div className="vehicles-grid">
        {vehicles.map(vehicle => (
          <div key={vehicle.id} className="vehicle-card">
            <div className="vehicle-card-header">
              <div className="vehicle-main-info">
                <div className="vehicle-icon">
                  <Truck size={24} />
                </div>
                <div>
                  <h3>{vehicle.name}</h3>
                  <span className="vehicle-number">{vehicle.vehicle_number}</span>
                </div>
              </div>
              <div className="vehicle-actions">
                <button onClick={() => handleOpenModal(vehicle)} className="btn-icon">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDelete(vehicle.id)} className="btn-icon btn-danger">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="vehicle-card-body">
              <div className="vehicle-info-row">
                <span className="info-label">Type:</span>
                <span className="vehicle-type-badge">{vehicleTypeLabels[vehicle.type] || vehicle.type}</span>
              </div>

              {vehicle.plate && (
                <div className="vehicle-info-row">
                  <span className="info-label">Plaque:</span>
                  <span className="plate-display">
                    <Tag size={14} />
                    {vehicle.plate}
                  </span>
                </div>
              )}

              <div className="vehicle-info-row">
                <span className="info-label">Statut:</span>
                <span className={`status-badge status-${vehicle.status}`}>
                  {statusLabels[vehicle.status] || vehicle.status}
                </span>
              </div>

              {vehicle.assigned_team_id && (
                <div className="vehicle-team">
                  <strong>Équipe assignée:</strong> {getTeamName(vehicle.assigned_team_id)}
                </div>
              )}

              {vehicle.service_capabilities.length > 0 && (
                <div className="vehicle-capabilities">
                  {vehicle.service_capabilities.slice(0, 3).map((cap, idx) => (
                    <span key={idx} className="capability-tag">{cap}</span>
                  ))}
                  {vehicle.service_capabilities.length > 3 && (
                    <span className="capability-more">+{vehicle.service_capabilities.length - 3}</span>
                  )}
                </div>
              )}

              {vehicle.capacity_notes && (
                <div className="vehicle-notes">
                  <small>{vehicle.capacity_notes}</small>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h3>{editingVehicle ? 'Modifier le véhicule' : 'Nouveau véhicule'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Numéro de véhicule *</label>
                <input
                  type="text"
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                  required
                  disabled={!!editingVehicle}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Truck 31, Van 12..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="pickup">Pickup</option>
                    <option value="van">Fourgonnette</option>
                    <option value="truck">Camion</option>
                    <option value="trailer">Remorque</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Plaque d'immatriculation</label>
                <input
                  type="text"
                  value={formData.plate}
                  onChange={(e) => setFormData({ ...formData, plate: e.target.value.toUpperCase() })}
                  placeholder="ABC-123"
                />
              </div>

              <div className="form-group">
                <label>Compétences / Services</label>
                <div className="capabilities-input">
                  <input
                    type="text"
                    value={newCapability}
                    onChange={(e) => setNewCapability(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCapability())}
                    placeholder="Ajouter une compétence..."
                  />
                  <button type="button" onClick={handleAddCapability} className="btn-add-small">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="capabilities-list">
                  {formData.service_capabilities.map((cap, idx) => (
                    <span key={idx} className="capability-tag">
                      {cap}
                      <button type="button" onClick={() => handleRemoveCapability(cap)}>&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Capacité / Équipement</label>
                <input
                  type="text"
                  value={formData.capacity_notes}
                  onChange={(e) => setFormData({ ...formData, capacity_notes: e.target.value })}
                  placeholder="Réservoir A/B, Remorque, etc."
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Informations supplémentaires..."
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingVehicle ? 'Mettre à jour' : 'Créer'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;